// EditClinic.js
import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { notify } from '../utils/notify'
import { Form, Input, Button, Select, Checkbox, Spin, Modal, Switch, Upload, Image, Row, Col, Card, DatePicker, Tooltip } from 'antd'
import { UploadOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { getClinicDetails, updateClinic, checkPhoneExists } from '../services/ClinicsService'
import useDebounce from '../hooks/useDebounce'
import { validateForm } from '../utils/validateForms'
import { faxFrequency, faxBookingTypeSync, clinicPartnerTypes, faxSequencesV2, faxFrequency2, faxSequencesV3 } from '../utils/constants';
import { getStates } from '../services/StateService'
import FormItem from 'antd/es/form/FormItem'
import { getAllGHLTagMaster } from '../services/GHLTagMasterService'
import { updateStateOfTagMaster } from '../utils/GHLTagMasterUpdateState'
import dayjs from 'dayjs'

const { Option } = Select

const typesOfScreen = [
  { value: 'Obgyn', text: 'EPDS' },
  { value: 'PHQ9-GAD7', text: 'PHQ9-GAD7' },
  { value: 'Geriatric', text: 'GDS' }
]

const ehrSystem = ['AthenaHealth', 'eCW', 'eCW - Femwell', 'Epic', 'Cerner', 'AllScripts']

// Add new tech stack options
const electronicMedicalRecordSystems = ['AthenaHealth', 'eCW-Femwell', 'eCW', 'Epic', 'Cerner', 'Other', 'Unknown']
const patientIntakeSystems = ['AdvancedMD', 'Connect', 'eCW', 'Emitrr', 'Kyruus', 'Phreesia', 'Other', 'Unknown']
const patientEngagementSystems = ['Epic MyChart', 'athenahealth', 'NextGen Healthcare', 'eClinicalWorks', 'Klara', 'Relatient', 'TeleVox', 'Kareo', 'Other', 'Unknown']
const patientScreeningSystems = ['CHADIS', 'Neuroflow', 'Other', 'Unknown']

const EditClinic = () => {
  const { id } = useParams()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const [states, setStates] = useState([])
  const [sourceOrganizationType, setSourceOrganizationType] = useState([])
  const [sourceOrganizationName, setSourceOrganizationName] = useState([])
  const [specialty, setSpecialty] = useState([])
  const [clinic, setClinic] = useState(null)
  const [loading, setLoading] = useState(false)
  const [logoImage, setLogoImage] = useState('')
  const [ehrName, setEhrName] = useState('')
  const [fileList, setFileList] = useState([])
  const [isFaxScheduled, setIsFaxScheduled] = useState(false);
  const [isNotOnboarded, setIsNotOnboarded] = useState(false)
  const [canTextPhone, setCanTextPhone] = useState(false)
  const [isBHI, setIsBHI] = useState(false)

  // Add state for tech stack selections
  const [emrSystem, setEmrSystem] = useState('')
  const [patientIntakeSystem, setPatientIntakeSystem] = useState('')
  const [patientEngagementSystem, setPatientEngagementSystem] = useState('')
  const [patientScreeningSystem, setPatientScreeningSystem] = useState('')
  
  // Add state for source organization "Other" selections
  const [sourceOrganizationTypeSelection, setSourceOrganizationTypeSelection] = useState('')
  const [sourceOrganizationNameSelection, setSourceOrganizationNameSelection] = useState('')
  
  // Add state for specialty "Other" selection
  const [specialtySelection, setSpecialtySelection] = useState('')

  // Phone uniqueness state (mirrors CarePartnerForm.jsx)
  const [phoneValue, setPhoneValue] = useState('')
  const [validatingPhone, setValidatingPhone] = useState(false)
  const [phoneConflict, setPhoneConflict] = useState(null)
  const debouncedPhone = useDebounce(phoneValue, 500)

  useEffect(() => {
    const digits = (debouncedPhone || '').replace(/\D/g, '')
    if (digits.length < 10 || !id) {
      setPhoneConflict(null)
      return
    }

    const checkPhone = async () => {
      setValidatingPhone(true)
      try {
        const { exists, clinic: matched } = await checkPhoneExists(debouncedPhone, id)
        setPhoneConflict(exists ? matched : null)
        if (exists) form.validateFields(['phone'])
      } catch (error) {
        console.error('Error checking phone:', error)
      } finally {
        setValidatingPhone(false)
      }
    }

    checkPhone()
  }, [debouncedPhone, form, id])

  const loadData = async () => {
    const state = await getStates()
    setStates(state)
    const ghlTagMasters = await getAllGHLTagMaster()
    if (ghlTagMasters) {
      updateStateOfTagMaster(
        ghlTagMasters,
        setSourceOrganizationType,
        setSourceOrganizationName,
        setSpecialty
      )
    }
    return { state, ghlTagMasters }
  }

  const fetchData = async (loadedData = null) => {
    try {
      const clinicData = await getClinicDetails(id)
      setClinic(clinicData)
      
      // If we have loaded data, use it to set form field values
      if (loadedData && loadedData.ghlTagMasters) {
        // Extract the source organization types and names from the loaded data
        let sourceOrgTypes = []
        let sourceOrgNames = []
        let specialties = []
        
        loadedData.ghlTagMasters.forEach((data) => {
          switch (data?.type) {
            case 'Source Organization Type':
              sourceOrgTypes = data?.value || []
              break
            case 'Source Organization Name':
              sourceOrgNames = data?.value || []
              break
            case 'Specialty':
              specialties = data?.value || []
              break
            default:
              break
          }
        })
        
        setFormsFieldValues(clinicData, sourceOrgTypes, sourceOrgNames, specialties)
      } else {
        setFormsFieldValues(clinicData)
      }
    } catch (error) {
      Modal.confirm({
        title: 'Error',
        content: 'Oops! Error in fetching care partner data',
        okText: 'Ok',
        cancelButtonProps: { style: { display: 'none' } }
      })
      console.error('Error fetching clinic details:', error)
    }
  }

  useEffect(() => {
    setLoading(true)
    const initializeData = async () => {
      try {
        const loadedData = await loadData()
        await fetchData(loadedData)
      } catch (error) {
        console.error('Error initializing data:', error)
      } finally {
        setLoading(false)
      }
    }
    initializeData()
  }, [])

  const setFormsFieldValues = (data, sourceOrgTypes = [], sourceOrgNames = [], specialties = []) => {
    setIsFaxScheduled(data?.isFaxScheduled)
    form.setFieldValue('name', data?.name)
    form.setFieldValue('streetName', data?.streetName)
    form.setFieldValue('city', data?.city)
    form.setFieldValue('address', data?.address)
    form.setFieldValue('zip', data?.zip)
    form.setFieldValue('phone', data?.phone)
    form.setFieldValue('faxNumber', data?.faxNumber)
    data?.faxSequence && data?.faxSequence.length > 0 && form.setFieldValue('faxSequence', data?.faxSequence)
    form.setFieldValue('isFaxScheduled', data?.isFaxScheduled)
    data?.clinicans && data?.clinicans.length > 0 && form.setFieldValue('clinicans', data?.clinicans)
    data?.typesOfScreen && data?.typesOfScreen.length > 0 && form.setFieldValue('typesOfScreen', data?.typesOfScreen)
    form.setFieldValue('ehr', data?.ehr)
    form.setFieldValue('ehrId', data?.ehrId)
    form.setFieldValue('typesOfIntegration', data?.typesOfIntegration === 'Sync Data' ? true : false)
    form.setFieldValue('allowReferral', data?.allowReferral)
    form.setFieldValue('referralLink', data?.referralLink)
    form.setFieldValue('alias', data?.alias)
    form.setFieldValue('faxFrequency', data?.faxFrequency);
    form.setFieldValue('faxBookingTypeSync', data?.faxBookingTypeSync);
    
    // Set source organization fields
    if (data['Source Organization Type']) {
      if (sourceOrgTypes.includes(data['Source Organization Type'])) {
        form.setFieldValue('sourceOrganizationType', data['Source Organization Type'])
        setSourceOrganizationTypeSelection(data['Source Organization Type'])
      } else {
        // Value is not in predefined options, set to "Other" and populate the "Other" field
        form.setFieldValue('sourceOrganizationType', 'Other')
        form.setFieldValue('sourceOrganizationTypeOther', data['Source Organization Type'])
        setSourceOrganizationTypeSelection('Other')
      }
    }
    
    if (data['Source Organization Name']) {
      if (sourceOrgNames.includes(data['Source Organization Name'])) {
        form.setFieldValue('sourceOrganizationName', data['Source Organization Name'])
        setSourceOrganizationNameSelection(data['Source Organization Name'])
      } else {
        // Value is not in predefined options, set to "Other" and populate the "Other" field
        form.setFieldValue('sourceOrganizationName', 'Other')
        form.setFieldValue('sourceOrganizationNameOther', data['Source Organization Name'])
        setSourceOrganizationNameSelection('Other')
      }
    }
    
    // Handle specialty "Other" option
    if (data['Specialty']) {
      if (specialties.includes(data['Specialty'])) {
        form.setFieldValue('specialty', data['Specialty'])
        setSpecialtySelection(data['Specialty'])
      } else {
        // Value is not in predefined options, set to "Other" and populate the "Other" field
        form.setFieldValue('specialty', 'Other')
        form.setFieldValue('specialtyOther', data['Specialty'])
        setSpecialtySelection('Other')
      }
    }
    
    data?.logo?.data && setLogoImage(btoa(String.fromCharCode(...new Uint8Array(data?.logo.data))))
    data['partnerType'] && form.setFieldValue('partnerType', data['partnerType'])
    // data['reviewReportSequence'] && form.setFieldValue('reviewReportSequence', data['reviewReportSequence'])
    // data['reviewReportFrequency'] && form.setFieldValue('reviewReportFrequency', data['reviewReportFrequency'])
    data['referralRecepientReportFrequency'] && form.setFieldValue('referralRecepientReportFrequency', data['referralRecepientReportFrequency'])
    data['assessmentScreeningSequence'] && form.setFieldValue('assessmentScreeningSequence', data['assessmentScreeningSequence'])
    data['assessmentScreeningFrequency'] && form.setFieldValue('assessmentScreeningFrequency', data['assessmentScreeningFrequency'])
    data['referralStatusSequence'] && form.setFieldValue('referralStatusSequence', data['referralStatusSequence'])
    data['referralStatusFrequency'] && form.setFieldValue('referralStatusFrequency', data['referralStatusFrequency'])
    data?.populationsServed && form.setFieldValue('populationsServed', data?.populationsServed.split(','))
    setEhrName(data?.ehr)
    
    data?.portalAddDate && form.setFieldValue('portalAddDate', dayjs(data?.portalAddDate, 'DD-MM-YYYY'))
    if (data?.clinicOnboardDate && dayjs(data?.clinicOnboardDate, 'DD-MM-YYYY').isValid()) {
      setIsNotOnboarded(false)
      form.setFieldValue('clinicOnboardDate', dayjs(data?.clinicOnboardDate, 'DD-MM-YYYY'))
      form.setFieldValue('notOnboarded', false)
    } else {
      setIsNotOnboarded(true)
      form.setFieldValue('clinicOnboardDate', null)
      form.setFieldValue('notOnboarded', true)
    }
    data?.lunaJoyRep && form.setFieldValue('lunaJoyRep', data?.lunaJoyRep)
    data?.clinicPointOfContactNotes && form.setFieldValue('clinicPointOfContact', data?.clinicPointOfContactNotes)
    data?.clinicPointOfContactPhone && form.setFieldValue('clinicPointOfContactPhone', data?.clinicPointOfContactPhone)
    form.setFieldValue('canTextPhone', data?.canTextPhone || false)
    setCanTextPhone(data?.canTextPhone || false)
    form.setFieldValue('isBHI', data?.isBHI || false)
    setIsBHI(data?.isBHI || false)

    // Set tech stack fields
    if (data?.patientIntakeSystem) {
      if (patientIntakeSystems.includes(data.patientIntakeSystem)) {
        form.setFieldValue('patientIntakeSystem', data.patientIntakeSystem)
        setPatientIntakeSystem(data.patientIntakeSystem)
      } else {
        // Value is not in predefined options, set to "Other" and populate the "Other" field
        form.setFieldValue('patientIntakeSystem', 'Other')
        form.setFieldValue('patientIntakeSystemOther', data.patientIntakeSystem)
        setPatientIntakeSystem('Other')
      }
    }
    
    if (data?.patientEngagementSystem) {
      if (patientEngagementSystems.includes(data.patientEngagementSystem)) {
        form.setFieldValue('patientEngagementSystem', data.patientEngagementSystem)
        setPatientEngagementSystem(data.patientEngagementSystem)
      } else {
        // Value is not in predefined options, set to "Other" and populate the "Other" field
        form.setFieldValue('patientEngagementSystem', 'Other')
        form.setFieldValue('patientEngagementSystemOther', data.patientEngagementSystem)
        setPatientEngagementSystem('Other')
      }
    }
    
    if (data?.patientScreeningSystem) {
      if (patientScreeningSystems.includes(data.patientScreeningSystem)) {
        form.setFieldValue('patientScreeningSystem', data.patientScreeningSystem)
        setPatientScreeningSystem(data.patientScreeningSystem)
      } else {
        // Value is not in predefined options, set to "Other" and populate the "Other" field
        form.setFieldValue('patientScreeningSystem', 'Other')
        form.setFieldValue('patientScreeningSystemOther', data.patientScreeningSystem)
        setPatientScreeningSystem('Other')
      }
    }
    
    // Set EMR system (mapped from ehr field for backward compatibility)
    if (data?.ehr) {
      if (electronicMedicalRecordSystems.includes(data.ehr)) {
        form.setFieldValue('electronicMedicalRecordSystem', data.ehr)
        setEmrSystem(data.ehr)
      } else {
        // Value is not in predefined options, set to "Other" and populate the "Other" field
        form.setFieldValue('electronicMedicalRecordSystem', 'Other')
        form.setFieldValue('electronicMedicalRecordSystemOther', data.ehr)
        setEmrSystem('Other')
      }
    }
  }

  const onFinish = async (data) => {
    setLoading(true)
    
    // Format the fax number for validation
    if (data.faxNumber) {
      const cleanFaxNumber = data.faxNumber.replace(/[^0-9]/g, '');
      if (cleanFaxNumber.length === 10) {
        // For display in the form we use (XXX) XXX-XXXX format
        // But for validateForm we need to ensure it can extract the digits
        data.faxNumber = cleanFaxNumber;
      }
    }
    
    const errors = validateForm(data)
    if (Object.keys(errors).length === 0) {
      // Retrieve the user object from local storage
      const user = JSON.parse(localStorage.getItem('user'))

      const formData = new FormData()
      formData.append('name', data?.name)
      data.streetName && formData.append('streetName', data?.streetName)
      data.city && formData.append('city', data?.city)
      formData.append('address', data?.address)
      data.zip && formData.append('zip', data?.zip)
      data.phone && formData.append('phone', data?.phone)
      formData.append('currentUser', user.name)
      data?.populationsServed && formData.append('populationsServed', data?.populationsServed)
      fileList?.length && data?.logo && data?.logo.fileList.length > 0 && formData.append('logo', data?.logo.fileList[0].originFileObj)
      formData.append('faxNumber', data?.faxNumber || '')
      data?.faxSequence && formData.append('faxSequence', data?.faxSequence)
      formData.append('isFaxScheduled', data?.isFaxScheduled || false)
      data?.clinicans && formData.append('clinicans', data?.clinicans)
      data?.landingPageUrl && formData.append('landingPageUrl', data?.landingPageUrl)
      data?.typesOfScreen && formData.append('typesOfScreen', data?.typesOfScreen)
      data?.ehr && formData.append('ehr', data?.ehr)
      data?.ehrId && formData.append('ehrId', data?.ehrId)
      formData.append('alias', data?.alias || '')
      data?.typesOfIntegration && formData.append('typesOfIntegration', data.typesOfIntegration ? 'Sync Data' : 'None')
      data?.referralLink && formData.append('referralLink', data.referralLink)
      data.partnerType && formData.append('partnerType', data?.partnerType);
      
      // Handle "Other" options for source organization fields
      if (data.sourceOrganizationType) {
        const orgTypeValue = data.sourceOrganizationType === 'Other' && data.sourceOrganizationTypeOther 
          ? data.sourceOrganizationTypeOther 
          : data.sourceOrganizationType;
        formData.append('sourceOrganizationType', orgTypeValue);
      }
      
      if (data.sourceOrganizationName) {
        const orgNameValue = data.sourceOrganizationName === 'Other' && data.sourceOrganizationNameOther 
          ? data.sourceOrganizationNameOther 
          : data.sourceOrganizationName;
        formData.append('sourceOrganizationName', orgNameValue);
      }
      
      // Handle "Other" option for specialty
      if (data.specialty) {
        const specialtyValue = data.specialty === 'Other' && data.specialtyOther 
          ? data.specialtyOther 
          : data.specialty;
        formData.append('specialty', specialtyValue);
      }
      
      data.allowReferral ? formData.append('allowReferral', data?.allowReferral) : formData.append('allowReferral', 'false')

      // Add new fields
      data.portalAddDate && formData.append('portalAddDate', data.portalAddDate.format('DD-MM-YYYY'))
      if (data.clinicOnboardDate && !isNotOnboarded) {
        formData.append('clinicOnboardDate', data.clinicOnboardDate.format('DD-MM-YYYY'))
      }
      data.lunaJoyRep && formData.append('lunaJoyRep', data.lunaJoyRep)
      data.clinicPointOfContact && formData.append('clinicPointOfContactNotes', data.clinicPointOfContact)
      data.clinicPointOfContactPhone && formData.append('clinicPointOfContactPhone', data.clinicPointOfContactPhone)
      formData.append('canTextPhone', canTextPhone)
      formData.append('isBHI', isBHI)

      // Add tech stack fields
      // Handle "Other" options for tech stack fields
      if (data.patientIntakeSystem) {
        const intakeValue = data.patientIntakeSystem === 'Other' && data.patientIntakeSystemOther 
          ? data.patientIntakeSystemOther 
          : data.patientIntakeSystem;
        formData.append('patientIntakeSystem', intakeValue);
      }
      
      if (data.patientEngagementSystem) {
        const engagementValue = data.patientEngagementSystem === 'Other' && data.patientEngagementSystemOther 
          ? data.patientEngagementSystemOther 
          : data.patientEngagementSystem;
        formData.append('patientEngagementSystem', engagementValue);
      }
      
      if (data.patientScreeningSystem) {
        const screeningValue = data.patientScreeningSystem === 'Other' && data.patientScreeningSystemOther 
          ? data.patientScreeningSystemOther 
          : data.patientScreeningSystem;
        formData.append('patientScreeningSystem', screeningValue);
      }
      
      // Map electronicMedicalRecordSystem to ehr for backward compatibility
      if (data.electronicMedicalRecordSystem) {
        const ehrValue = data.electronicMedicalRecordSystem === 'Other' && data.electronicMedicalRecordSystemOther 
          ? data.electronicMedicalRecordSystemOther 
          : data.electronicMedicalRecordSystem;
        formData.append('ehr', ehrValue);
      }

      if (isFaxScheduled) {
        data.faxBookingTypeSync && formData.append('faxBookingTypeSync', data.faxBookingTypeSync)
        data.faxFrequency && formData.append('faxFrequency', data.faxFrequency)
        data.faxBookingTypeSync && formData.append('reviewReportSequence', data.faxBookingTypeSync)
        data.faxFrequency && formData.append('reviewReportFrequency', data.faxFrequency)
        data.referralRecepientReportFrequency && formData.append('referralRecepientReportFrequency', data.referralRecepientReportFrequency)
        data.assessmentScreeningSequence && formData.append('assessmentScreeningSequence', data.assessmentScreeningSequence)
        data.assessmentScreeningFrequency && formData.append('assessmentScreeningFrequency', data.assessmentScreeningFrequency)
        data.referralStatusSequence && formData.append('referralStatusSequence', data.referralStatusSequence)
        data.referralStatusFrequency && formData.append('referralStatusFrequency', data.referralStatusFrequency)
      } 
      

      try {
        await updateClinic(id, formData)
        notify(`"${data?.name}" Clinic Updated Successfully!`)
        navigate('/clinic')
      } catch (error) {
        Modal.error({
          title: 'Failed to update clinic details',
          content: (
            <div>
              <p>{error?.response?.data?.error?.message}</p>
            </div>
          ),
          okText: 'Ok',
          cancelButtonProps: { style: { display: 'none' } }
        })
      }
      setLoading(false)
    } else {
      setLoading(false)
      const errorMessages = Object.values(errors).map((error, index) => {
        if (typeof error == 'object') {
          return error.map((err, i) => <p style={{ marginBottom: 0 }} key={`${i}_${index}`}>* {err}</p>);
        } else {
          return (<div key={index}>* {error}</div>)
        }
      })
      Modal.confirm({
        title: 'Error',
        content: (
          <div>
            {!errors.clinicans && <p>Oops! Can not update data due to the following errors:</p>}
            {errorMessages}
          </div>
        ),
        okText: 'Ok',
        cancelButtonProps: { style: { display: 'none' } }
      })
    }
  }

  const goBack = () => {
    navigate('/clinic')
  }

  const formItemLayout = {
    labelCol: { span: 7 },
    wrapperCol: { span: 16 }
  }

  if (!clinic) {
    return <div>Loading...</div>
  }

  const getImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const img = document.createElement('img')
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      img.onload = () => {
        canvas.width = img.width
        canvas.height = img.height
        ctx.drawImage(img, 0, 0, img.width, img.height)

        const width = canvas.width
        const height = canvas.height

        resolve({ width, height })
      }

      img.onerror = () => {
        reject('Error loading image')
      }

      img.src = URL.createObjectURL(file)
    })
  }

  const handleFileUpload = async ({ file, onSuccess, onError }) => {
    if (file.size > 90 * 1024) {
      onError('File size should be less than 90kb')
      setFileList([])
      Modal.confirm({
        title: 'Error',
        content: (
          <div>
            <p>File size should be less than 90kb</p>
          </div>
        ),
        okText: 'Ok',
        cancelButtonProps: { style: { display: 'none' } }
      })
    } else {
      const dimensions = await getImageDimensions(file)
      if (dimensions.width <= 750 && dimensions.height <= 500) {
        setFileList([file])
        onSuccess('ok')
      } else {
        onError('This image does not meet the required resolution.')
        setFileList([])
        Modal.confirm({
          title: 'Error',
          content: (
            <div>
              <p>This image does not meet the required resolution.</p>
            </div>
          ),
          okText: 'Ok',
          cancelButtonProps: { style: { display: 'none' } }
        })
      }
    }
  }

  const handleSwitchChange = (checked) => {
    setIsFaxScheduled(checked); 
  };

  // Add validation functions
  const validatePhoneNumber = (_, value) => {
    if (!value) return Promise.resolve();

    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(value.replace(/[^\d]/g, ''))) {
      return Promise.reject(new Error('Please enter a valid 10-digit phone number'));
    }
    return Promise.resolve();
  };

  const validatePhoneUniqueness = (_, value) => {
    if (!value) return Promise.resolve();
    if (phoneConflict) {
      return Promise.reject(new Error(`A clinic with this phone number already exists${phoneConflict?.name ? ` (${phoneConflict.name})` : ''}`));
    }
    return Promise.resolve();
  };

  const validateFaxNumber = (_, value) => {
    if (!value) return Promise.resolve();
    
    // Strip all non-digit characters and check if we have 10 digits
    const cleanedValue = value.replace(/[^\d]/g, '');
    if (cleanedValue.length !== 10) {
      return Promise.reject(new Error('Please enter a valid 10-digit fax number'));
    }
    return Promise.resolve();
  };

  const validateZipCode = (_, value) => {
    if (!value) return Promise.resolve();
    
    // US ZIP code validation (5 digits or 5+4 format)
    const zipRegex = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
    if (!zipRegex.test(value)) {
      return Promise.reject(new Error('Please enter a valid ZIP code (00000 or 00000-0000 format)'));
    }
    return Promise.resolve();
  };

  const validateCity = (_, value) => {
    if (!value) return Promise.resolve();
    
    // City should only contain letters, spaces, hyphens and periods (for St.)
    const cityRegex = /^[a-zA-Z\s.\-]+$/;
    if (!cityRegex.test(value)) {
      return Promise.reject(new Error('Please enter a valid city name'));
    }
    return Promise.resolve();
  };


  // Add validation for clinic onboard date
  const validateClinicOnboardDate = (_, value) => {
    if (!value && !isNotOnboarded) {
      return Promise.reject(new Error('Please enter a clinic onboard date or check "Not Onboarded"'));
    }
    return Promise.resolve();
  };

  // Add a function to format phone numbers as they're typed
  const formatPhoneNumber = (value) => {
    if (!value) return value;
    
    // Strip all non-digit characters
    const phoneNumber = value.replace(/[^\d]/g, '');
    
    // Format: (XXX) XXX-XXXX
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  // Add a function to format fax numbers as they're typed (similar to phone)
  const formatFaxNumber = (value) => {
    if (!value) return value;
    
    // Strip all non-digit characters
    const faxNumber = value.replace(/[^\d]/g, '');
    
    // Format: (XXX) XXX-XXXX
    if (faxNumber.length <= 3) {
      return faxNumber;
    } else if (faxNumber.length <= 6) {
      return `(${faxNumber.slice(0, 3)}) ${faxNumber.slice(3)}`;
    } else {
      return `(${faxNumber.slice(0, 3)}) ${faxNumber.slice(3, 6)}-${faxNumber.slice(6, 10)}`;
    }
  };

  // Add validation for integers only
  const validateIntegersOnly = (_, value) => {
    if (!value) return Promise.resolve();
    
    // Only allow integers (whole numbers)
    const integerRegex = /^[0-9]+$/;
    if (!integerRegex.test(value)) {
      return Promise.reject(new Error('Please enter only numbers (no decimals or characters)'));
    }
    return Promise.resolve();
  };

  return (
    <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        width: '100%',
        padding: '0 16px'
      }}>
      <div style={{ 
        width: '100%',
        maxWidth: '1200px'
      }}>
        <h1 style={{ marginBlock: '20px', textAlign: 'center' }}>Edit Clinic</h1>
        <div style={{ 
                textAlign: "center", 
                marginTop: "10px", 
                marginBottom: "10px",
            }}>
                <p style={{ 
                    fontSize: "14px", 
                    color: "#495057", 
                    margin: "0",
                    lineHeight: "1.5"
                }}>
                    <a 
                        href="https://docs.google.com/document/d/1rhV9Peb94qnaB96N1e0UnF71Q6aj5Oyc1_D445PCr7c/edit?tab=t.0" 
                        target="_blank" 
                        rel="noreferrer"
                        style={{ 
                            color: "#1976d3", 
                            textDecoration: "none",
                            fontWeight: "500"
                        }}
                        onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                        onMouseLeave={(e) => e.target.style.textDecoration = "none"}
                    >
                        Click here to review instructions on how to onboard a clinic
                    </a>
                </p>
            </div>
        <Spin spinning={loading}>
          <Form 
            form={form} 
            onFinish={onFinish} 
            layout="vertical"
            labelAlign="left"
            colon={false}
            requiredMark="optional"
          >
            <Card 
              title="Care Partner Information" 
              style={{ 
                marginBottom: 24, 
                borderRadius: '8px', 
                border: '1px solid #d9d9d9',
                backgroundColor: '#f9f9ff'
              }}
              headStyle={{ 
                fontSize: '18px', 
                fontWeight: 'bold',
                backgroundColor: '#eceeff',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px'
              }}
            >
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="name"
                    label="Clinic/Referring Party Name"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter the name.'
                      }
                    ]}
                  >
                    <Input />
                  </Form.Item>
                  <div style={{
                    fontSize: "12px",
                    color: "#666",
                    marginTop: "-16px",
                    marginBottom: "16px",
                    fontStyle: "italic"
                  }}>
                    Name changes also update the linked GHL B2B contact's Company Name
                  </div>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <Form.Item 
                    name="specialty" 
                    label="Specialty"
                    rules={[
                      {
                        required: true,
                        message: 'Please select a specialty.'
                      }
                    ]}>
                    <Select
                      placeholder="Select a specialty"
                      onChange={(value) => setSpecialtySelection(value)}
                      filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    >
                      {specialty?.map((element) => (
                        <Option key={element} value={element}>
                          {element}
                        </Option>
                      ))}
                      <Option key="Other" value="Other">
                        Other
                      </Option>
                    </Select>
                  </Form.Item>
                  {specialtySelection === 'Other' && (
                    <Form.Item
                      name="specialtyOther"
                      label="Specify Specialty"
                      rules={[
                        {
                          required: true,
                          message: 'Please specify the specialty.'
                        }
                      ]}
                    >
                      <Input placeholder="Enter specialty" />
                    </Form.Item>
                  )}
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <Form.Item 
                    name="streetName" 
                    label="Address"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter the address.'
                      }
                    ]}
                  >
                    <Input placeholder="Enter address" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item name="sourceOrganizationType" label="Partner Type">
                    <Select
                      placeholder="Select a partner type"
                      onChange={(value) => setSourceOrganizationTypeSelection(value)}
                      filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    >
                      {sourceOrganizationType?.map((element) => (
                        <Option key={element} value={element}>
                          {element}
                        </Option>
                      ))}
                      <Option key="Other" value="Other">
                        Other
                      </Option>
                    </Select>
                  </Form.Item>
                  {sourceOrganizationTypeSelection === 'Other' && (
                    <Form.Item
                      name="sourceOrganizationTypeOther"
                      label="Specify Partner Type"
                      rules={[
                        {
                          required: true,
                          message: 'Please specify the partner type.'
                        }
                      ]}
                    >
                      <Input placeholder="Enter partner type" />
                    </Form.Item>
                  )}
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="sourceOrganizationName" label="Corporate Entity">
                    <Select
                      placeholder="Select a corporate entity"
                      onChange={(value) => setSourceOrganizationNameSelection(value)}
                      filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    >
                      {sourceOrganizationName?.map((element) => (
                        <Option key={element} value={element}>
                          {element}
                        </Option>
                      ))}
                      <Option key="Other" value="Other">
                        Other
                      </Option>
                    </Select>
                  </Form.Item>
                  {sourceOrganizationNameSelection === 'Other' && (
                    <Form.Item
                      name="sourceOrganizationNameOther"
                      label="Specify Corporate Entity"
                      rules={[
                        {
                          required: true,
                          message: 'Please specify the corporate entity.'
                        }
                      ]}
                    >
                      <Input placeholder="Enter corporate entity" />
                    </Form.Item>
                  )}
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} sm={8} md={6}>
                  <Form.Item 
                    name="city" 
                    label="City"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter the city.'
                      },
                      { validator: validateCity }
                    ]}
                  >
                    <Input placeholder="Enter city" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <Form.Item
                    name="address"
                    label="State"
                    rules={[
                      {
                        required: true,
                        message: 'Please select the state.'
                      }
                    ]}
                  >
                    <Select
                      placeholder="Select a state"
                      filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    >
                      {states.map((state) => (
                        <Option key={state.code} value={state.code}>
                          {state.title}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <Form.Item 
                    name="zip" 
                    label="Zip"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter the zip code.'
                      },
                      { validator: validateZipCode }
                    ]}
                  >
                    <Input placeholder="Enter zip code" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <Form.Item
                    name="phone"
                    label="Clinic Phone"
                    hasFeedback
                    validateStatus={validatingPhone ? 'validating' : (phoneConflict ? 'error' : undefined)}
                    rules={[
                      {
                        required: true,
                        message: 'Please enter the phone number.'
                      },
                      { validator: validatePhoneNumber },
                      { validator: validatePhoneUniqueness }
                    ]}
                  >
                    <Input
                      placeholder="Enter phone number"
                      onChange={(e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        form.setFieldsValue({ phone: formatted });
                        setPhoneValue(formatted);
                        if (phoneConflict) setPhoneConflict(null);
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item name="populationsServed" label="Populations Served">
                    <Select
                      placeholder="Select populations served"
                      mode="multiple"
                      filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    >
                      {states.map((state) => (
                        <Option key={state.code} value={state.code}>
                          {state.title}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item 
                    name="faxNumber" 
                    label="Fax number"
                    rules={[
                      { validator: validateFaxNumber },
                      {
                        required: isFaxScheduled,
                        message: 'Fax number is required when fax scheduling is enabled'
                      }
                    ]}
                  >
                    <Input 
                      addonBefore="+1" 
                      onChange={(e) => {
                        const formatted = formatFaxNumber(e.target.value);
                        form.setFieldsValue({ faxNumber: formatted });
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item name="clinicans" label="Clinicians" 
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value || value.length === 0) return Promise.resolve();

                          // Normalize spaces: trim and collapse multiple spaces to one
                          const normalizedValue = value.map(clinician =>
                            clinician
                              .replace(/\s+/g, ' ') // collapse multiple spaces
                              .trim()
                          );

                          // Check for numbers in any clinician name
                          const invalidNames = normalizedValue.filter(
                            clinician => /\d/.test(clinician)
                          );

                          if (invalidNames.length > 0) {
                            return Promise.reject(
                              new Error('Clinician names cannot contain numbers')
                            );
                          }
                          form.setFieldsValue({ clinicans: normalizedValue });

                          return Promise.resolve();
                        }
                      }
                    ]}
                  >
                    <Select 
                      mode="tags" 
                      placeholder="Add clinicians (format: Dr Lastname)"
                      tokenSeparators={[',']} 
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="partnerType" label="Clinic Partner Type">
                    <Select placeholder="Select a clinic partner type">
                      {clinicPartnerTypes?.map((type) => (
                        <Option key={type.value} value={type.value}>
                          {type.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item name="typesOfScreen" label="Type of screener">
                    <Select placeholder="Select a screener type" mode="multiple">
                      {typesOfScreen.map((screener) => (
                        <Option key={screener.value} value={screener.value}>
                          {screener.text}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="isFaxScheduled" label="Schedule fax">
                    <Switch onChange={handleSwitchChange} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="isBHI"
                    label="BHI Clinic"
                    valuePropName="checked"
                    tooltip="Mark this clinic as a Behavioral Health Integration (BHI) clinic. Referrals from BHI clinics are flagged in GHL so they can be routed to BHI Coaches."
                  >
                    <Switch
                      checkedChildren="BHI"
                      unCheckedChildren="No"
                      onChange={(checked) => setIsBHI(checked)}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Form.Item label="Logo" name="logo">
                      <Upload
                        maxCount={1}
                        fileList={fileList}
                        onRemove={(value) => {
                          setFileList([])
                        }}
                        customRequest={handleFileUpload}
                        listType="text"
                        accept=".jpg,.jpeg,.png"
                      >
                        <Button icon={<UploadOutlined />}>Upload logo</Button>
                        <div>
                          <small>The image size must be less than 90 KB, and dimensions less than 750 x 500 pixels.</small>
                        </div>
                      </Upload>
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} md={12}>
                  {logoImage && (
                    <FormItem label="Previous logo">
                      <Image width={170} src={`data:image/jpeg;base64,${logoImage}`} alt={clinic.name} />
                    </FormItem>
                  )}
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="portalAddDate"
                    label="Portal Add Date"
                    rules={[
                      {
                        required: true,
                        message: 'Please select the portal add date.'
                      }
                    ]}
                  >
                    <DatePicker 
                      format="DD-MM-YYYY"
                      style={{ width: '100%' }}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="clinicOnboardDate"
                    label="Clinic Onboard Date"
                    rules={[
                      { validator: validateClinicOnboardDate }
                    ]}
                    style={{ marginBottom: 8 }}
                    required
                  >
                    <DatePicker 
                      format="DD-MM-YYYY"
                      style={{ width: '100%' }}
                      disabled={isNotOnboarded}
                    />
                  </Form.Item>
                  <Form.Item
                    name="notOnboarded"
                    valuePropName="checked"
                    style={{ marginTop: -4 }}
                  >
                    <Checkbox 
                      onChange={(e) => {
                        setIsNotOnboarded(e.target.checked);
                        if (e.target.checked) {
                          form.setFieldsValue({ clinicOnboardDate: null });
                        }
                      }}
                    >
                      Not Onboarded{' '}
                      <Tooltip title="Clinics are considered onboarded the date when they agree to a formal relationship with LunaJoy and have a LunaJoy rep">
                        <QuestionCircleOutlined style={{ color: '#1890ff', marginLeft: 4 }} />
                      </Tooltip>
                    </Checkbox>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="lunaJoyRep"
                    label="LunaJoy Representative"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter the LunaJoy representative.'
                      }
                    ]}
                  >
                    <Input placeholder="Enter LunaJoy representative" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="clinicPointOfContact"
                    label="Clinic Point of Contact & Notes"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter the clinic point of contact and notes.'
                      }
                    ]}
                  >
                    <Input.TextArea 
                      placeholder="Enter clinic point of contact and notes"
                      rows={3}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="clinicPointOfContactPhone"
                    label="Clinic Point of Contact Phone"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter the clinic point of contact phone number.'
                      },
                      { validator: validatePhoneNumber }
                    ]}
                     style={{ marginBottom: 8 }}
                  >
                    <Input 
                      placeholder="Enter phone number"
                      onChange={(e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        form.setFieldsValue({ clinicPointOfContactPhone: formatted });
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="canTextPhone"
                    valuePropName="checked"
                    style={{ marginTop: 8 }}
                  >
                    <Checkbox 
                      onChange={(e) => setCanTextPhone(e.target.checked)}
                    >
                      Can we text this phone number?
                    </Checkbox>
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Clinic Tech Section */}
            <Card 
              title="Clinic Tech" 
              style={{ 
                marginBottom: 24, 
                borderRadius: '8px', 
                border: '1px solid #d9d9d9',
                backgroundColor: '#fff9f9'
              }}
              headStyle={{ 
                fontSize: '18px', 
                fontWeight: 'bold',
                backgroundColor: '#ffecec',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px'
              }}
            >
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="electronicMedicalRecordSystem"
                    label="Electronic Medical Record System"
                  >
                    <Select
                      placeholder="Select EMR system"
                      onChange={(value) => setEmrSystem(value)}
                      filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    >
                      {electronicMedicalRecordSystems.map((system) => (
                        <Option key={system} value={system}>
                          {system}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  {emrSystem === 'Other' && (
                    <Form.Item
                      name="electronicMedicalRecordSystemOther"
                      label="Specify EMR System"
                      rules={[
                        {
                          required: true,
                          message: 'Please specify the EMR system.'
                        }
                      ]}
                    >
                      <Input placeholder="Enter EMR system name" />
                    </Form.Item>
                  )}
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="patientIntakeSystem"
                    label="Patient Intake System"
                  >
                    <Select
                      placeholder="Select patient intake system"
                      onChange={(value) => setPatientIntakeSystem(value)}
                      filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    >
                      {patientIntakeSystems.map((system) => (
                        <Option key={system} value={system}>
                          {system}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  {patientIntakeSystem === 'Other' && (
                    <Form.Item
                      name="patientIntakeSystemOther"
                      label="Specify Patient Intake System"
                      rules={[
                        {
                          required: true,
                          message: 'Please specify the patient intake system.'
                        }
                      ]}
                    >
                      <Input placeholder="Enter patient intake system name" />
                    </Form.Item>
                  )}
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="patientEngagementSystem"
                    label="Patient Engagement System"
                  >
                    <Select
                      placeholder="Select patient engagement system"
                      onChange={(value) => setPatientEngagementSystem(value)}
                      filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    >
                      {patientEngagementSystems.map((system) => (
                        <Option key={system} value={system}>
                          {system}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  {patientEngagementSystem === 'Other' && (
                    <Form.Item
                      name="patientEngagementSystemOther"
                      label="Specify Patient Engagement System"
                      rules={[
                        {
                          required: true,
                          message: 'Please specify the patient engagement system.'
                        }
                      ]}
                    >
                      <Input placeholder="Enter patient engagement system name" />
                    </Form.Item>
                  )}
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="patientScreeningSystem"
                    label="Patient Screening System"
                  >
                    <Select
                      placeholder="Select patient screening system"
                      onChange={(value) => setPatientScreeningSystem(value)}
                      filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    >
                      {patientScreeningSystems.map((system) => (
                        <Option key={system} value={system}>
                          {system}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  {patientScreeningSystem === 'Other' && (
                    <Form.Item
                      name="patientScreeningSystemOther"
                      label="Specify Patient Screening System"
                      rules={[
                        {
                          required: true,
                          message: 'Please specify the patient screening system.'
                        }
                      ]}
                    >
                      <Input placeholder="Enter patient screening system name" />
                    </Form.Item>
                  )}
                </Col>
              </Row>
            </Card>

            {/* Fax Scheduling Section */}
            {isFaxScheduled && (
              <Card 
                title="Fax Scheduling Configuration" 
                style={{ 
                  marginBottom: 24, 
                  borderRadius: '8px', 
                  border: '1px solid #d9d9d9',
                  backgroundColor: '#f9fff9'
                }}
                headStyle={{ 
                  fontSize: '18px', 
                  fontWeight: 'bold',
                  backgroundColor: '#ecffec',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px'
                }}
              >
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="faxBookingTypeSync"
                      label="Review Report Sequence"
                      rules={[
                        {
                          required: isFaxScheduled,
                          message: "Please select review report sequence",
                        },
                      ]}
                    >
                      <Select placeholder="Select a review report sequence">
                        {faxBookingTypeSync?.map((type) => (
                          <Option key={type.value} value={type.value}>
                            {type.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.faxBookingTypeSync !== currentValues.faxBookingTypeSync
                      }
                    >
                      {({ getFieldValue }) =>
                        getFieldValue("faxBookingTypeSync") ? (
                          <Form.Item
                            name="faxFrequency"
                            label="Review Report Frequency"
                            rules={[
                              {
                                required: isFaxScheduled,
                                message: "Please select review report frequency",
                              },
                            ]}
                          >
                            <Select placeholder="Select a review report frequency">
                              {faxFrequency?.map((freq) => (
                                <Option key={freq.value} value={freq.value}>
                                  {freq.label}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        ) : null
                      }
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item 
                      name="referralRecepientReportFrequency" 
                      label="Referral Recipient Report Frequency"
                      rules={[{
                        required: isFaxScheduled,
                        message: 'Please select referral recipient report frequency'
                      }]}
                    >
                      <Select placeholder="Select Referral Recipient Report Frequency">
                        {faxFrequency2?.map((freq) => (
                          <Option key={freq.value} value={freq.value}>
                            {freq.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="assessmentScreeningSequence"
                      label="Assessment Screening Sequence"
                      rules={[
                        {
                          required: isFaxScheduled,
                          message: "Please select assessment screening sequence",
                        },
                      ]}
                    >
                      <Select placeholder="Select an assessment screening sequence">
                        {faxSequencesV3.map((fax) => (
                          <Option key={fax} value={fax}>
                            {fax}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.assessmentScreeningSequence !==
                        currentValues.assessmentScreeningSequence
                      }
                    >
                      {({ getFieldValue }) =>
                        getFieldValue("assessmentScreeningSequence") ? (
                          <Form.Item
                            name="assessmentScreeningFrequency"
                            label="Assessment Screening Frequency"
                            rules={[
                              {
                                required: isFaxScheduled,
                                message: "Please select assessment screening frequency",
                              },
                            ]}
                          >
                            <Select placeholder="Select Assessment Screening Frequency">
                              {faxFrequency?.map((freq) => (
                                <Option key={freq.value} value={freq.value}>
                                  {freq.label}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        ) : null
                      }
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="referralStatusSequence"
                      label="Referral Status Sequence"
                      rules={[
                        {
                          required: isFaxScheduled,
                          message: "Please select referral status sequence",
                        },
                      ]}
                    >
                      <Select placeholder="Select a referral status sequence">
                        {faxSequencesV2.map((fax) => (
                          <Option key={fax} value={fax}>
                            {fax}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.referralStatusSequence !== currentValues.referralStatusSequence
                      }
                    >
                      {({ getFieldValue }) =>
                        getFieldValue("referralStatusSequence") ? (
                          <Form.Item
                            name="referralStatusFrequency"
                            label="Referral Status Frequency"
                            rules={[
                              {
                                required: isFaxScheduled,
                                message: "Please select referral status frequency",
                              },
                            ]}
                          >
                            <Select placeholder="Select referral status frequency">
                              {faxFrequency2?.map((freq) => (
                                <Option key={freq.value} value={freq.value}>
                                  {freq.label}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        ) : null
                      }
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            )}

            <Card 
              title="EHR Integration" 
              style={{ 
                marginBottom: 24, 
                borderRadius: '8px', 
                border: '1px solid #d9d9d9',
                backgroundColor: '#fdf9ff'
              }}
              headStyle={{ 
                fontSize: '18px', 
                fontWeight: 'bold',
                backgroundColor: '#f5ecff',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px'
              }}
            >
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item name="alias" label="Clinic Name in EHR">
                    <Input />
                  </Form.Item>
                  <div>
                    <small>*(Provide the clinic name sent by EHR)</small>
                  </div>
                </Col>
              </Row>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.electronicMedicalRecordSystem !== currentValues.electronicMedicalRecordSystem
                }
              >
                {({ getFieldValue }) => {
                  const emrSystem = getFieldValue("electronicMedicalRecordSystem");
                  const isAthenaHealth = emrSystem === 'AthenaHealth' || (emrSystem === 'Other' && getFieldValue("electronicMedicalRecordSystemOther")?.toLowerCase().includes('athena'));
                  
                  return isAthenaHealth ? (
                    <>
                      <div style={{ 
                        marginTop: 16, 
                        marginBottom: 16, 
                        fontWeight: 'bold',
                        padding: '8px 12px',
                        backgroundColor: '#f9f0ff',
                        borderRadius: '4px'
                      }}>
                        AthenaHealth Configuration
                      </div>
                      <Row gutter={24}>
                        <Col xs={24} md={12}>
                          <Form.Item name="ehrId" label="Practice ID" rules={[
                            { validator: validateIntegersOnly }
                          ]}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item 
                            name="typesOfIntegration" 
                            label="Enable sync"
                            valuePropName="checked"
                            style={{ marginBottom: 16 }}
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col xs={24} md={12}>
                          <Form.Item 
                            label="Athena referral" 
                            name="allowReferral" 
                            valuePropName="checked"
                          >
                            <Checkbox>Enable Athena referral functionality</Checkbox>
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  ) : null;
                }}
              </Form.Item>
            </Card>

            {/* Back and Submit Buttons */}
            <Row>
              <Col xs={24} style={{ textAlign: 'right', marginBottom: 40 }}>
                <Button size="large" onClick={goBack} style={{ marginRight: 10, fontWeight: 'semibold' }}>Back</Button>
                <Button 
                  size="large" 
                  type="primary" 
                  htmlType="submit" 
                  style={{ padding: '0 30px', fontWeight: 'semibold' }}
                >
                  Save Changes
                </Button>
              </Col>
            </Row>
          </Form>
        </Spin>
      </div>
    </div>
  )
}

export default EditClinic
